import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { listingSuppliesAPI, checkoutAPI } from "@/lib/api";
import { toast } from "sonner";
import { Package, Truck, Loader2 } from "lucide-react";

const LABELS = {
  not_ordered: { text: "Supplies not ordered", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  pending: { text: "Supplies unpaid", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  paid: { text: "Supplies paid", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" },
  shipped: { text: "Supplies shipped", cls: "border-primary/40 bg-primary/10 text-primary" },
  not_needed: { text: "Self-packaging", cls: "border-border bg-secondary text-muted-foreground" },
};

export function SuppliesStatusBadge({ status, testId }) {
  const s = LABELS[status] || LABELS.not_ordered;
  return (
    <Badge className={`border ${s.cls}`} data-testid={testId}>
      <Package className="mr-1 h-3 w-3" /> {s.text}
    </Badge>
  );
}

export default function OrderSuppliesButton({ listing, size = "sm", variant = "default", onDone, testId }) {
  const [status, setStatus] = useState(listing?.supplies_status || "not_ordered");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setStatus(listing?.supplies_status || "not_ordered"); }, [listing?.id, listing?.supplies_status]);

  if (listing?.has_original_packaging) {
    return <div className="flex min-h-9 items-center"><SuppliesStatusBadge status="not_needed" testId={testId ? `${testId}-badge` : undefined} /></div>;
  }
  if (status === "paid" || status === "shipped") {
    return <div className="flex min-h-9 items-center"><SuppliesStatusBadge status={status} testId={testId ? `${testId}-badge` : undefined} /></div>;
  }

  const startOrder = async () => {
    setBusy(true);
    try {
      const res = await listingSuppliesAPI.orderSupplies(listing.id);
      if (res.data?.already_paid) {
        toast.success("Supplies already paid");
        setStatus("paid");
        onDone?.();
        return;
      }
      const paymentId = res.data?.shipping_payment_id;
      if (!paymentId) throw new Error("Missing shipping payment id");
      const sess = await checkoutAPI.createSession("supplies", paymentId);
      const url = sess.data?.checkout_url;
      if (!url) throw new Error("Missing checkout url");
      window.location.assign(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start supplies order");
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-9 items-center gap-2">
      <SuppliesStatusBadge status={status} testId={testId ? `${testId}-badge` : undefined} />
      <Button
        size={size}
        variant={variant}
        onClick={startOrder}
        disabled={busy}
        className={variant === "default" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}
        data-testid={testId || `order-supplies-${listing.id}`}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Truck className="mr-1 h-3.5 w-3.5" /> Order supplies</>}
      </Button>
    </div>
  );
}
